import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { StringParam, useQueryParam } from 'use-query-params';
import { alpha, Button, Grid, Typography } from '@material-ui/core';
import ArrowBackIosIcon from '@material-ui/icons/ArrowBackIos';
import CheckIcon from '@material-ui/icons/Check';
import { StateNodeType, SubscriptionType } from '@sierralabs/state-machine';
import { useFormContext } from '../FormContext';
import { useFormDragContext } from '../FormDragContext';
import FormElement from './FormElement';
const StyledDiv = styled.div `
  position: relative;
  .section-grid {
    // NOTE: don't set margin-left / margin-right due to MUI grid margins
    margin-top: 12px;
    margin-bottom: 12px;
  }
  .form-element-placeholder {
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px dashed ${props => props.theme.palette.primary.main};
    border-radius: 3px;
    padding: 24px 12px;
    margin: 0 8px;
  }
  .back-button-container {
    padding-bottom: 24px;
  }
  .event-button {
    margin-right: 12px;
    &:last-child {
      margin-right: 0;
    }
  }
  .final {
    display: flex;
    align-items: center;
    margin-top: 8px;
    padding: 6px;
    background: ${props => alpha(props.theme.palette.primary.main, 0.1)};
    border-radius: 4px;
    font-weight: bold;
    .icon {
      margin-right: 8px;
    }
  }
  .edit-transition {
    border: 1px dashed ${props => props.theme.palette.primary.main};
  }
`;
export const FormSection = ({ stateNode, edit, hasBack, backTitle = 'Back', appendSectionControls, disableInlineSave, disabled, hideTransitions, onMouseOver, onTouchMove, onTransition, onBack }) => {
    var _a, _b, _c;
    const { disableDataSources, formState, formValues, setFormState, addElement, stateMachine, transitionButton } = useFormContext();
    const isSelected = formState.selectedStateNode === stateNode;
    const { dragElementProps } = useFormDragContext();
    // handle selecting event transition button
    const [, setSelectedEventTransition] = useQueryParam('event-transition', StringParam);
    // Check which next events can transition
    const mountRef = useRef(false);
    const [nextEvents, setNextEvents] = useState([]);
    const updateNextEvents = useCallback(() => {
        if (!(stateMachine === null || stateMachine === void 0 ? void 0 : stateMachine.currentState))
            return;
        stateMachine === null || stateMachine === void 0 ? void 0 : stateMachine.nextEvents().then(nextEvents => {
            // prevent unmount component state change situation
            if (mountRef.current) {
                setNextEvents(nextEvents);
            }
        });
    }, [stateMachine]);
    useEffect(() => {
        if (!stateMachine)
            return;
        mountRef.current = true;
        // don't subscribe if in edit mode
        if (edit)
            return;
        updateNextEvents(); // make sure to check next events on mount
        const onEnd = () => {
            updateNextEvents();
        };
        stateMachine.subscribe(SubscriptionType.TransitionEnd, onEnd);
        return () => {
            mountRef.current = false;
            stateMachine.unsubscribe(SubscriptionType.TransitionEnd, onEnd);
        };
    }, [edit, stateMachine, updateNextEvents]);
    const { meta } = stateNode;
    // check if all required fields are completed
    const hasCompletedSection = useMemo(() => {
        for (const element of (meta === null || meta === void 0 ? void 0 : meta.elements) || []) {
            const value = formValues[element.dataSource || element.key];
            if (element.required &&
                (typeof value === 'undefined' ||
                    (Array.isArray(value) && value.length === 0))) {
                return false;
            }
        }
        return true;
    }, [formValues, meta === null || meta === void 0 ? void 0 : meta.elements]);
    return (React.createElement(StyledDiv, { className: `form-section${isSelected ? ' selected' : ''}`, onMouseOver: event => edit ? onMouseOver === null || onMouseOver === void 0 ? void 0 : onMouseOver(event, stateNode) : undefined, onTouchMove: event => edit ? onTouchMove === null || onTouchMove === void 0 ? void 0 : onTouchMove(event, stateNode) : undefined },
        (meta === null || meta === void 0 ? void 0 : meta.showBackButton) && hasBack && (React.createElement("div", { className: 'back-button-container' },
            React.createElement(Button, { startIcon: React.createElement(ArrowBackIosIcon, null), size: 'small', onClick: () => {
                    onBack === null || onBack === void 0 ? void 0 : onBack();
                } }, backTitle))),
        !(meta === null || meta === void 0 ? void 0 : meta.hideTitle) && (React.createElement(Typography, { variant: 'h5' }, (meta === null || meta === void 0 ? void 0 : meta.title) || 'Untitled Section')),
        (meta === null || meta === void 0 ? void 0 : meta.description) && React.createElement(Typography, null, meta.description),
        React.createElement(Grid, { container: true, spacing: 2, className: 'section-grid' }, (_a = meta === null || meta === void 0 ? void 0 : meta.elements) === null || _a === void 0 ? void 0 :
            _a.map((element, index) => {
                const disabledField = (disableDataSources || []).some(source => element.dataSource && element.dataSource === source);
                return (React.createElement(FormElement, Object.assign({ "data-index": index, key: index, edit: edit, stateNode: stateNode, element: element, disabled: disabled || disabledField, disableInlineSave: disableInlineSave }, dragElementProps, { onClick: event => {
                        if (edit) {
                            event.stopPropagation();
                            setFormState(state => (Object.assign(Object.assign({}, state), { selectedStateNode: stateNode, selectedFormElement: element })));
                        }
                    }, onClickAdd: () => addElement(stateNode, undefined, index) })));
            }),
            edit && (!(meta === null || meta === void 0 ? void 0 : meta.elements) || meta.elements.length === 0) && (React.createElement(Grid, { item: true, xs: 12, className: 'form-element-placeholder' },
                React.createElement(Button, { variant: 'outlined', color: 'primary', onClick: () => addElement(stateNode) }, "Add Form Element"))),
            !hideTransitions && (React.createElement(Grid, { item: true, xs: 12, className: 'transition-buttons' }, (_b = stateNode === null || stateNode === void 0 ? void 0 : stateNode.events) === null || _b === void 0 ? void 0 :
                _b.filter(e => !e.isTransient).map((event, index) => {
                    var _a;
                    const TransitionButton = (transitionButton || Button);
                    return (React.createElement(TransitionButton, { event: event, key: `${event.name}_${index}`, variant: 'contained', className: 'event-button', color: 'primary', 
                        // TOOD: canTransition is a promise; so need to do async
                        disabled: !edit &&
                            (!hasCompletedSection ||
                                !((_a = nextEvents.find(e => e.name === event.name)) === null || _a === void 0 ? void 0 : _a.canTransition)), onClick: () => {
                            if (edit) {
                                setSelectedEventTransition(event.name);
                            }
                            else {
                                onTransition === null || onTransition === void 0 ? void 0 : onTransition(event);
                            }
                        } }, event.name || 'New Event'));
                }),
                appendSectionControls,
                edit && (stateNode === null || stateNode === void 0 ? void 0 : stateNode.type) === StateNodeType.Final ? (React.createElement("div", { className: 'final' },
                    React.createElement(CheckIcon, { className: 'icon' }),
                    "Form Completed")) : ((!(stateNode === null || stateNode === void 0 ? void 0 : stateNode.events) || ((_c = stateNode === null || stateNode === void 0 ? void 0 : stateNode.events) === null || _c === void 0 ? void 0 : _c.length) === 0) &&
                    edit && (React.createElement(Button, { className: 'edit-transition', variant: 'outlined', color: 'primary', onClick: () => { } }, "Add Transitions"))))))));
};
export default FormSection;
//# sourceMappingURL=FormSection.js.map